import { useLayoutEffect, useRef, useState, type CSSProperties } from "react";
import { addDays, formatMonthYear, formatWeekday, isoWeek, startOfWeek, timePart } from "../format";
import { coversDay, shortNames, type GridEvent, type GridPerson } from "./PersonWeekGrid";
import { timetableChange, timetableClasses } from "../timetablePresentation";
import { importedSourceLabel } from "../importedSources";

const WEEKDAYS = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];

/**
 * Monatsübersicht: Wochen als Zeilen, Tage als Spalten, füllt die verfügbare Fläche. Jeder Eintrag beginnt mit den
 * Kürzeln der Personen (Farbe ist nie die einzige Kennzeichnung); passt nicht alles in einen Tag, führt „+N“ zur Woche.
 */
export function MonthGrid({
  persons,
  events,
  monthStart,
  today,
  onOpenDay,
}: {
  persons: GridPerson[];
  events: GridEvent[];
  monthStart: string;
  today: string;
  /** Details eines Tages öffnen. */
  onOpenDay: (day: string) => void;
}) {
  const short = shortNames(persons);
  const byId = new Map(persons.map((p) => [p.id, p]));
  const month = monthStart.slice(0, 7);
  const gridStart = startOfWeek(monthStart);
  const weeks: string[] = [];
  for (let w = gridStart; w.slice(0, 7) <= month; w = addDays(w, 7)) weeks.push(w);
  const sorted = [...events].sort((a, b) => Number(b.allDay) - Number(a.allDay) || a.start.localeCompare(b.start));

  // Wie viele Einträge passen in einen Tag? Aus der gemessenen Zellhöhe statt fester Annahmen.
  const ref = useRef<HTMLDivElement>(null);
  const [fit, setFit] = useState(4);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => {
      const cell = el.querySelector<HTMLElement>(".mcal-day");
      const head = el.querySelector<HTMLElement>(".mcal-date");
      if (!cell || !head || cell.clientHeight === 0) return;
      // Höhe eines vorhandenen Eintrags (auf dem Handy zweizeilig), sonst aus der Schriftgröße geschätzt.
      const sample = el.querySelector<HTMLElement>(".mcal-chip");
      const chip = (sample?.offsetHeight ?? (parseFloat(getComputedStyle(cell).fontSize) || 16) * 1.55) + 3;
      setFit(Math.max(1, Math.floor((cell.clientHeight - head.offsetHeight - 42) / chip)));
    };
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [weeks.length, events.length]);

  const isForAll = (e: GridEvent) => persons.length > 1 && persons.every((p) => e.personIds.includes(p.id));

  return (
    <div className="mcal" ref={ref} style={{ "--mcal-weeks": weeks.length } as CSSProperties} role="table" aria-label={`Monatsübersicht ${formatMonthYear(monthStart)}`}>
      <div className="mcal-corner" role="columnheader" title={formatMonthYear(monthStart)}>{Number(monthStart.slice(5, 7))}/{monthStart.slice(2, 4)}</div>
      {WEEKDAYS.map((d, i) => (
        <div key={d} className={`mcal-weekday${i >= 5 ? " weekend" : ""}`} role="columnheader">{d}</div>
      ))}
      {weeks.map((week) => (
        <div key={week} className="mcal-week" role="row">
          <button type="button" className="mcal-kw" role="rowheader" onClick={() => onOpenDay(week)} title={`Montag der Woche ${isoWeek(week)} öffnen`}>
            {isoWeek(week)}
          </button>
          {Array.from({ length: 7 }, (_, i) => addDays(week, i)).map((day) => {
            const items = sorted.filter((e) => coversDay(e, day));
            const shown = items.length > fit ? items.slice(0, fit - 1) : items;
            const hidden = items.length - shown.length;
            const outside = day.slice(0, 7) !== month;
            const changed = items.filter(e => timetableChange(e.source, e.title) === "changed").length;
            const cancelled = items.filter(e => timetableChange(e.source, e.title) === "cancelled").length;
            return (
              <div
                key={day}
                role="cell"
                aria-label={`${formatWeekday(day)}, ${Number(day.slice(8))}.${day.slice(5, 7)}., ${items.length} Termine`}
                className={`mcal-day${day === today ? " today" : ""}${outside ? " outside" : ""}${day < today ? " past" : ""}`}
                onClick={() => onOpenDay(day)}
              >
                <button className="mcal-date" aria-label={`${formatWeekday(day)}, ${day}, Tagesansicht`} onClick={ev => { ev.stopPropagation(); onOpenDay(day); }}>{Number(day.slice(8))}</button>
                <div className="mcal-alerts" aria-label={`${changed} geändert, ${cancelled} entfallen`}>
                  {changed > 0 && <span className="changed" title={`${changed} geändert`}>↻ {changed}</span>}
                  {cancelled > 0 && <span className="cancelled" title={`${cancelled} entfallen`}>× {cancelled}</span>}
                </div>
                {shown.map((e) => {
                  const all = isForAll(e);
                  const ids = e.personIds.filter((id) => byId.has(id));
                  const color = all || ids.length === 0 ? "var(--primary)" : byId.get(ids[0])!.color;
                  return (
                    <button
                      key={e.key}
                      type="button"
                      className={`mcal-chip${e.allDay ? " allday" : ""}${timetableClasses(e.source, e.title)}`}
                      data-source={e.source}
                      style={{ borderLeftColor: `var(--event-accent, ${color})`, background: `var(--event-bg, color-mix(in srgb, ${color} 16%, var(--surface)))` }}
                      disabled={!e.onSelect}
                      title={[importedSourceLabel(e.source), e.title, e.allDay ? "ganztägig" : timePart(e.start), all ? "Alle" : ids.map((id) => byId.get(id)!.name).join(", ")].filter(Boolean).join(" · ")}
                      onClick={(ev) => {
                        ev.stopPropagation();
                        e.onSelect?.();
                      }}
                    >
                      <span className="who" aria-label={all ? "Alle" : ids.map((id) => byId.get(id)!.name).join(", ")}>
                        {all ? (
                          <span className="mini all">Alle</span>
                        ) : (
                          ids.map((id) => <span key={id} className="mini" style={{ background: byId.get(id)!.color }}>{short.get(id)}</span>)
                        )}
                      </span>
                      {!e.allDay && <span className="t">{timePart(e.start)}</span>}
                      <span className="title">{e.title}</span>
                    </button>
                  );
                })}
                {hidden > 0 && (
                  <button type="button" className="mcal-more" onClick={(ev) => { ev.stopPropagation(); onOpenDay(day); }}>
                    +{hidden} weitere
                  </button>
                )}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}
